<!--
    QDirectory tree view

    USAGE:
      <ExplorerDirectory
        v-on:directory-selection="myfunction(new_dirs)"
        @update="QFilters"
        :filters="explorer_filters"
      ></ExplorerDirectory>

    Public methods
       reset_selection() → Initialize internal filters values
-->

<template>
  <div>
    <div class="explorerdirectory-cont">

      <!-- Header -->
      <div class="explorerdirectory-header pl-2">
        <v-simple-checkbox color="primary" :value="select_all" @input="set_select_all" />

        <v-btn icon @click="expand_all = !expand_all">
          <v-icon>
            {{ expand_all ? 'mdi-folder-open-outline' : 'mdi-folder-outline' }}
          </v-icon>
        </v-btn>

        <ExplorerDirectoryEditable
          :filters="filters"
          :directories="current_centro_directory_list"
          @update="folders_updated"
        ></ExplorerDirectoryEditable>

        <SearchTextTable @input="setSearchText" />
      </div>

      <v-expansion-panels
        v-model="panel"
        accordion
        multiple
      >
        <v-expansion-panel
          v-for="cdir in qcentrodir_iter"
          :key="cdir.id"
        >
          <v-expansion-panel-header
            class="ed-expansion-header"
            v-if="is_multicentro"
          >
            <div class="ed-expansion-content">
              <CentroLogo
                :centro_id="cdir.id"
                style='width: 38px; height: 38px'
              />
              <span style="padding-left: 12px">
                {{ cdir.name }}
              </span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="ed-panel">
            <v-treeview
              :value="selection_for(cdir.id)"
              dense
              :open="open_for(cdir.id)"
              :items="cdir.directories"
              :search="search"
              return-object
              selection-type="independent"
              :key="cdir.id + ':' + select_reactive"
              @update:open="(value) => update_open(cdir.id, value)"
            >
              <!-- To make selection-type more intuitive you can add to v-treeview:
                   selection-type="unicorn"
                   https://stackoverflow.com/a/66187402 >v2.4-->

              <template v-slot:prepend="{ item, selected, open }">
                <v-btn
                  icon
                  @click="toggle_selection(cdir.id, item)"
                  :class="selected ? 'accent--text' : ''"
                >
                  <v-icon medium>
                    {{ (selected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline') }}
                  </v-icon>
                </v-btn>
                <v-icon :color="item.color">
                  {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                </v-icon>
              </template>

              <template slot="label" slot-scope="{ item }">
                <a
                  class="black--text text-start"
                  @click="toggle_selection(cdir.id, item)"
                >
                  {{ item.name }}
                </a>
              </template>
            </v-treeview>
          </v-expansion-panel-content>

        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>


<style lang="scss">
 @import "@/sass/explorer.scss";
 @import '@/sass/modalform.scss';

 .explorerdirectory-cont {
   @extend .documentation-table-cont-base;
 }

 .explorerdirectory-header {
   @extend .documentation-table-header-base;
   display: flex;
   align-items: center;
 }

 .ed-expansion-content {
   display: flex;
   align-items: center;
   color: #000;
 }

 .ed-panel > .v-expansion-panel-content__wrap {
   margin: 0;
   padding: 0;
 }
</style>


<script>
 import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
 import QDirectory from '@/api/QDirectory';
 import ExplorerDirectoryPath from '@/components/explorer/ExplorerDirectoryPath';
 import SearchTextTable from '@/components/SearchTextTable';
 import debounce from '@/utils/debounce';
 import ExplorerDirectoryEditable from '@/components/explorer/ExplorerDirectoryEditable';
 import CentroLogo from '@/components/CentroLogo';
 import { QFilters, get_qfilter } from '@/qunit/filters';

 const DEFAULT_EXPAND = 5;

 function centro_sort(c1, c2) {
   return c1.path_str.localeCompare(c2.path_str);
 }

 export default {
   name: 'ExplorerDirectory',

   components: {
     ExplorerDirectoryPath,
     SearchTextTable,
     ExplorerDirectoryEditable,
     CentroLogo,
   },
   
   props: {
     filters: {},
   },

   data: () => ({
     expand_all: false,
     open: {},
     search: null,
     select_all: false,
     old_selected: [],
     panel: [], // Centro expansion panel
     centro_selection: {}, // {centro: [selection]}
     select_reactive: 0, // Update me with +1 every time selection changes!
   }),

   computed: {
     ...mapGetters([
       'site_id',
       'centro_id',
       'isManager',
       'isAdmin',
     ]),

     ...mapState([
       'site_centros',
       'centros',
       'qdirectory',
       'qdirectory_centro_list',
       'explorer_filters',
     ]),

     centros_list() {
       if (this.filters.get_filter) {
         let filtered_centros = (this.filters.get_filter('centro_list') || {}).value;
         if (Array.isArray(filtered_centros) && filtered_centros.length >= 0) {
           let res = (filtered_centros.map(cid => this.centros[cid])).sort(centro_sort);
           if (res.length > 0) {
             return res;
           }
         }
       }

       return [this.centros[this.centro_id]];
       /* return this.site_centros[this.site_id]; */
     },

     is_multicentro() {
       return (this.centros_list.length >= 2
            || (this.centros_list.length == 1
             && this.centros_list[0].id != this.centro_id));
     },

     qcentrodir_iter() {
       return (
         this.centros_list
             .filter(c => this.qdirectory_centro_list[c.id] !== undefined)
             .map(c => ({
               ...c,
               directories: this.qdirectory_centro_list[c.id]
             }))
             .sort((a, b) => {
               if (a.id === this.centro_id) { // current centro first
                 return -1;
               }
               a.path_str.localeCompare(b.path_str) // sort by path
             })
       );
     },

     qdirectory_map() {
       // Returns {dir_id: {props}} for all selectable centros
       let res = {};
       const cset = new Set(this.centros_list.map(c => c.id));
       for (const qdir of Object.values(this.qdirectory)) {
         if (cset.has(qdir.centro_id)) {
           res[qdir.id] = qdir;
         }
       }
       return res;
     },

     total_folders_count() {
       // Returns a number
       return Object.keys(this.qdirectory_map).length;
     },

     current_centro_directory_list() {
       // Returns the list for the user's centro
       return this.qdirectory_centro_list[this.centro_id];
     },
   },

   mounted() {
     this.select_reactive = 0;
     this.set_selection(this.filters.directories || [], false);
     this.auto_expand_panels();
   },

   methods: {
     ...mapMutations(['SET_ACTIVE_DIRECTORY']),

     folders_updated() {
       this.set_selection([], true);
     },

     auto_expand_panels(count) {
       // count is optional
       // count 0 → Contract all
       count = Math.min(this.total_folders_count, (count === undefined) ? DEFAULT_EXPAND : count);
       this.panel = [...Array(count).keys()] // [0, 1, 2... count]
     },

     search_in_selection(directory_id) {
       // Returns {centro: centro_id, index: index_position}
       // Returns null if not found
       let dirlist;
       for (const centro of this.centros_list) {
         dirlist = this.centro_selection[centro.id] || [];
         for (let i = 0; i < dirlist.length; i++) {
           if (dirlist[i].id === directory_id) {
             return {centro_id: centro.id, index: i};
           }
         }
       }
       return null;
     },

     selection_for(centro_id) {
       // Returns [{directory}, ...]
       return this.centro_selection[centro_id] || [];
     },

     set_selection(directory_ids, emit_event) {
       // Sets centro_selection to select directory_ids
       // (optional) emit_event true/false
       this.centro_selection = {};
       let dir;
       for (const dirid of directory_ids) {
         dir = this.qdirectory_map[dirid];
         if (this.centro_selection[dir.centro_id] === undefined) {
           this.centro_selection[dir.centro_id] = [];
         }
         this.centro_selection[dir.centro_id].push(dir);
       }
       this.selection_updated(emit_event || false);
     },

     toggle_selection(centro_id, value) {
       const pos = this.search_in_selection(value.id);
       if (pos === null) {
         if (this.centro_selection[centro_id] === undefined) {
           this.centro_selection[centro_id] = [];
         }
         this.centro_selection[centro_id].push(value);
       } else {
         this.centro_selection[pos.centro_id].splice(pos.index, 1);
       }
       this.selection_updated(true);
     },

     selection_flat() {
       // Returns [{dir_props}, ...]
       return Object.values(this.centro_selection).flat();
     },

     setSearchText(search_text) {
       this.search = search_text;
     },

     selection_updated(emit_event) {
       this.select_reactive++;
       this.select_all = (
         this.selection_flat().length >= Math.max(1, this.total_folders_count)
       );

       // Find the first selected folder of the user's centro
       let winner = null;
       for (const dir of this.selection_flat()) {
         if (dir.centro_id === this.centro_id) {
           winner = dir;
           break;
         }
       }
       this.SET_ACTIVE_DIRECTORY(winner === null ? {} : winner);

       if (emit_event) {
         debounce(this.emit_selection_update, 1000)();
       }
     },

     emit_selection_update() {
       this.$emit('update-selected_directories');
       let filters = new QFilters(this.filters);
       filters.set_filter(get_qfilter(
         'directories',
         this.selection_flat().map(d => d.id)
       ));
       this.$emit('update', filters);
     },

     reset_selection() {
       this.centro_selection = {};
       this.selection_updated();
     },

     select_all_folders() {
       this.centro_selection = {};
       this.set_selection(Object.values(this.qdirectory_map).map(d => d.id));
     },

     set_select_all(value) {
       if (value) {
         this.select_all_folders();
         this.selection_updated(true);
       } else {
         this.reset_selection();
         this.selection_updated(true);
       }
     },

     open_for(cid) {
       return this.open[cid] || [];
     },

     update_open(cid, value) {
       this.open[cid] = value;
     },
   },

   watch: {
     expand_all(expand) {
       if (expand) {
         this.open = {};
         Object.values(this.qdirectory).filter((d) => !!d.children);
         for (const dir of Object.values(this.qdirectory)) {
           if (!!dir.children && dir.children.length > 0) {
             if (this.open[dir.centro_id] === undefined) {
               this.open[dir.centro_id] = [];
             }
             this.open[dir.centro_id].push(dir);
           }
         }

       } else {
         this.open = {};
       }

       if (this.is_multicentro) {
         this.auto_expand_panels(expand ? 2000000 : 0);
       }
     },

     is_multicentro(after, before) {
       if (before && !after) {
         this.$nextTick(() => this.auto_expand_panels(1000));
       }
     }
   },
 };
</script>
